/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import usLocale from 'date-fns/locale/en-US';
import grLocale from 'date-fns/locale/el';
import * as _ from 'lodash';
import useAPI from 'hooks/api-hooks';
import SearchIcon from '@material-ui/icons/Search';
import { Grid,
  Typography,
  Divider,
  Button,
  Dialog,
  DialogTitle, DialogActions,
  DialogContent } from '@material-ui/core';
import AppTable from 'components/app/AppTable';
import { useTranslation } from 'react-i18next';
import { useItems } from 'models/atoms/items-hook';
import { useAlerts } from 'hooks/alerts-hooks';
import CollectionKeys from 'models/atoms/collection-keys';
import { useNavigation, useOptionSerial } from 'NavigationProvider';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import { LoaderSkeleton } from 'components/layout/KitRegistrationDashboard';
import CircularProgress from '@material-ui/core/CircularProgress';
import useMediaQueries from '../hooks/media-query-hooks';
import { results } from './Results';

export const useStyles = makeStyles((theme) => {
  return {
    BoostButton: {
      borderRadius: '15px',
      color: '#3B3B3B',
      background: '#f2b035',
      position: 'relative',
      textTransform: 'none',
      fontSize: '2ram',
      padding: '12px 15px',
      fontWeight: 400,
      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
      '&:hover': {
        background: '#ff802b',
        boxShadow: '0px 7px 14px rgba(0, 0, 0, 0.4)',
      },
    },
    titleCta: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    dialogButtonSuccess: {
      background: theme.palette.secondary.main,
      color: 'white',
      '&:hover': {
        background: '#ff802b',
      },
    },
    dialogButtonError: {
      background: theme.palette.error.main,
      color: 'white',
      '&:hover': {
        background: '#ff802b',
      },
    },
    loaderContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
    },
  };
});

export const LogoImageAndRepeat = ({ row }) => {
  const foundResult = results.find((result) => {
    return result.type === row.product
      || result.alt_type === row.product || result.other_alt_type === row.product;
  });

  if (!foundResult) {
    return null;
  }

  return (
    <div style={ { display: 'flex',
      flexDirection: 'column',
      overflowX: 'visible',
      whiteSpace: 'nowrap',
      position: 'relative' } }
    >
      {foundResult.title}
      <img
        src={ foundResult.icon }
        alt={ foundResult.title }
        style={ { width: '100px', height: '100px', ...foundResult.imageStyle } }
      />
    </div>
  );
};

const ResultsTable = ({ kits, search, setSearch, isLoading, pagination, refetch }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [ DialogOpen, setDialogOpen ] = useState(false);
  const [ secondDialogOpen, setSecondDialogOpen ] = useState(false);
  const [ name, setName ] = useState('');
  const [ boostSerial, setBoostSerial ] = useState('');
  const [ selectedSerial, setSelectedSerial ] = useState('');
  const [ disableButton, setDisableButton ] = useState(false);
  const [ loader, setLoader ] = useState(false);
  const [ popUploader, setPopUploader ] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const { navigateToPage } = useNavigation();
  // eslint-disable-next-line no-unused-vars
  const { setSerial } = useOptionSerial('3-in-1');
  const { client } = useAPI();
  const { alertError } = useAlerts();

  // function for the first kit boost request (POST {URL}/serial-numbers/upgrade)
  const getKitBoostSerial = async (serial) => {
    const result = await client.post('/serial-numbers/upgrade', {
      serial,
    });
    if (result?.serial !== undefined && 8 === result?.serial.length) {
      setBoostSerial(result?.serial);
    } else {
      alertError(t('kitBoostError'));
    }
    setDialogOpen(false);
    setLoader(false);
  };

  // function for the second kit boost request (POST {URL}/registration-kits/upgrade/correlate)
  const upgradeKit = async (ancestor, newSerial) => {
    setDisableButton(true);
    setPopUploader(true);
    const result = await client.post('/registration-kits/upgrade/correlate', {
      ancestor,
      new_serial: newSerial,
    });
    setDialogOpen(false);
    if (result?.serial_number_id?.serial !== undefined && 8 === result?.serial_number_id?.serial.length) {
      setPopUploader(false);
      setSecondDialogOpen(true);
    } else {
      setPopUploader(false);
      alertError(t('kitBoostError'));
    }
    setDisableButton(false);
  };

  const allKits = useMemo(() => {
    return kits?.filter((kit) => {
      return null != kit.thematic_package && Object.prototype.hasOwnProperty.call(kit.thematic_package, 'id');
    })
      .flatMap((kit, id) => {
        const foundKit = results.find((result) => {
          return result.type === kit.thematic_package.thematic_category_id.name
      || result.alt_type === kit.thematic_package.thematic_category_id.name || result.other_alt_type === kit.thematic_package.thematic_category_id.name;
        });

        let type = kit.thematic_package.name;
        if ('Cardio Health' === type || 'iDNA Cardio Health' === type || 'iDNA Cardio Health kit' === type) {
          type = 'IDNA Cardio Health';
        }

        return {
          id,
          kitId: kit.id,
          product: type,
          serial: kit.serial_number_id.serial,
          serial_id: kit.serial_number_id.id,
          serial_sample_available: kit.serial_number_id.sample_available,
          sendAnalysisReportStatus: kit.sendAnalysisReportStatus,
          sendDietReportStatus: kit.sendDietReportStatus,
          navTitle: foundKit?.navTitle,
          icon: foundKit?.icon,
          title: foundKit?.title,
          style: foundKit?.style,
          fields: foundKit?.fields,
          status: 'active',
          sendReportStatus: kit.sendReportStatus,
          familyName: kit.customer_id?.family_name ?? '',
          firstName: kit.customer_id?.name ?? '',
          email: kit.customer_id?.email ?? '',
          customerId: kit.customer_id?.id,
          upgraded: kit.serial_number_id?.upgraded_serial,
        };
      });
  }, [ kits, isLoading ]);

  const tableColumns = [
    { id: 1,
      label: t('product'),
      sortable: false,
      field: 'product',
      customRenderer({ row }) {
        return (
          <LogoImageAndRepeat row={ row } />
        );
      } },
    { id: 2,
      label: 'Serial Number',
      field: 'serial',
      sortable: false,
      customRenderer({ row }) {
        return (
          <div>
            {row.serial}
          </div>
        );
      } },
    { id: 3,
      label: t('patCustFirstLastName'),
      field: 'firstName',
      sortable: true,
      customRenderer({ row }) {
        return (
          <div style={ { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' } }>
            <span style={ { paddingLeft: '10px' } }>
              { row.firstName }
              {' '}
              { row.familyName }
            </span>
            <Button
              style={ { textTransform: 'none',
                fontSize: '14px',
                padding: 0,
                margin: 0,
                fontWeight: 400,
                textDecoration: 'underline',
                color: '#526D9C' } }
              onClick={ () => {
                setSerial(row.kitId);
                navigateToPage({ pageId: 'patientDetails' });
              } }
            >
              <span>
                <SearchIcon style={ { position: 'relative', top: '9px' } } />
                {t('Δες τα πλήρη στοιχεία ασθενή/πελάτη')}
              </span>
            </Button>
          </div>
        );
      } },

    { id: 4,
      label: t('kitBoostButton'),
      sortable: false,
      customRenderer({ row }) {
        // if already have kitbosted show this
        if (row?.upgraded !== undefined && 0 < row?.upgraded.length) {
          return (
            <div style={ { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' } }>
              {t('kitBoostRowText')}
              <br />
              {'→'}
              {row?.upgraded[0].serial}
            </div>
          );
        }
        // else if it can be kitboosted show this
        return (
          <div style={ { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' } }>
            <Button
              className={ classes.BoostButton }
              onClick={ async () => {
                setLoader(true);
                await getKitBoostSerial(row.serial);
                setSelectedSerial(row.serial);
                setName(`${ row?.firstName } ${ row?.familyName }`);
                setDialogOpen(true);
              } }
            >
              <span>
                <FlashOnIcon style={ { position: 'relative', top: '9px' } } />
                {t('kitBoostButton')}
              </span>
            </Button>
          </div>
        );
      } },
  ];

  return (
    <>
      {loader ? <LoaderSkeleton />
        : (
          <>
            <AppTable
              refetch={ refetch }
              columns={ tableColumns }
              data={ allKits }
              loading={ isLoading }
              searchProps={ {
                search,
                setSearch,
              } }
              paginationProps={ pagination }
            />
            <Dialog
              open={ DialogOpen }
              onClose={ () => { setDialogOpen(false); } }
            >
              <DialogTitle>
                {t('kitBoostActivateTitle')}
                {selectedSerial}
                .
              </DialogTitle>
              <DialogContent>
                { popUploader ? <div className={ classes.loaderContainer }><CircularProgress /></div>
                  : (
                    <div>
                      { t('kitBoostActivateSub') }
                    </div>
                  )}
              </DialogContent>
              <DialogActions style={ { gap: '20px', padding: '16px 24px' } }>
                <Button
                  disabled={ disableButton }
                  className={ classes.dialogButtonSuccess }
                  onClick={ async () => { await upgradeKit(selectedSerial, boostSerial); } }
                  autoFocus
                >
                  {t('ok')}
                </Button>
                <Button
                  disabled={ disableButton }
                  className={ classes.dialogButtonError }
                  onClick={ () => {
                    setDialogOpen(false);
                    setBoostSerial('');
                    setSelectedSerial('');
                  } }
                  autoFocus
                >
                  {t('cancel')}
                </Button>
              </DialogActions>
            </Dialog>
            <Dialog
              open={ secondDialogOpen }
              onClose={ () => { setSecondDialogOpen(false); } }
            >
              <DialogTitle>
                {t('kitBoostActivateTitle2')}
                {selectedSerial}
                .
              </DialogTitle>
              <DialogContent>
                { popUploader ? <div className={ classes.loaderContainer }><CircularProgress /></div>
                  : (
                    <div>
                      { t('kitBoostActivateSub2', { serial: selectedSerial, name, newSerial: boostSerial }) }
                    </div>
                  )}
              </DialogContent>
              <DialogActions style={ { gap: '20px', padding: '16px 24px' } }>
                <Button
                  disabled={ disableButton }
                  className={ classes.dialogButtonSuccess }
                  onClick={ async () => {
                    setSecondDialogOpen(false);
                    setBoostSerial('');
                    setSelectedSerial('');
                    setName('');
                    navigateToPage({ pageId: 'resultsDoctor' });
                  } }
                  autoFocus
                >
                  {t('ok')}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
    </>
  );
};

const KitBoostPageDesktop = () => {
  const classes = useStyles();

  const { isMd } = useMediaQueries();
  const { t, i18n } = useTranslation();

  const locale = 'en-US' === i18n.language ? usLocale : grLocale;

  const [ search, setSearch ] = useState('');

  const [ filterQuery, setFilterQuery ] = useState({ });

  useEffect(() => {
    let searchQueryKey = { };

    if ('' !== search) {
      searchQueryKey = {
        _where: {
          _or: [
            [{ 'serial_number_id.serial_contains': search }],
            [{ customer_id_null: false }, { 'customer_id.family_name_contains': search }],
          ],
        },
      };
    }

    setFilterQuery({
      ...searchQueryKey,
    });
  }, [ search ]);

  const {
    items: kits,
    itemsLoading: isLoading,
    pagination,
    refetch,
  } = useItems(CollectionKeys.DoctorSortedKits, { defaultPagination: { limit: 10, sort: { order: 'asc', orderBy: 'upgraded_serial' } },
    filterQuery: { ...filterQuery,
      'serial_number_id.ancestor_null': true,
      'serial_number_id.is_upgradeable': true,
      registrationStatus_eq: 'done',
      'thematic_package.id_in': [ 363, 361 ] } });

  return (
    <Grid container spacing={ isMd ? 4 : 2 }>
      <Grid item xs={ 12 }>
        <div className={ classes.titleCta }>
          <Typography variant='h6' color='textPrimary'>
            {t('KitBoost')}
          </Typography>
        </div>
      </Grid>
      <Grid item xs={ 12 }>
        <Divider />
      </Grid>

      <Grid container item xs={ 12 } style={ { paddingTop: '0px' } }>
        <Grid item xs={ 12 }>
          <ResultsTable
            pagination={ pagination }
            kits={ kits }
            search={ search }
            setSearch={ setSearch }
            refetch={ refetch }
            isLoading={ isLoading }
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

const KitBoostPage = () => {
  return (
    <div style={ { width: '100%' } }>
      <KitBoostPageDesktop />
    </div>
  );
};

export default KitBoostPage;
